import { sha256Hex } from "./local-ifc";

/**
 * Multi-file export into a single user-chosen directory. This extends the same user-initiated,
 * no-overwrite, read-back-verified discipline `local-export-save.ts` established for a single
 * file (see `bim-hub-integration/answers/2026-08-10-tauri-local-export-save-capability-go.md`) to
 * a batch of files that all belong together (for example several already-built local export
 * artifacts). The directory itself is never listed, traversed, or written to outside the exact
 * files this call is asked to write: the OS folder picker (`dialog:allow-open`, `directory: true`)
 * adds only the chosen directory to the Tauri fs runtime scope, the same mechanism the save
 * dialog already uses for a single file — no static `scope` entry and no `fs:default` /
 * directory-listing permission is added.
 */

export interface DirectoryExportArtifact {
  readonly filename: string;
  readonly bytes: Uint8Array;
}

export type DirectoryExportItemState = "saved" | "refused-existing" | "failed" | "cleanup-failed";

export interface DirectoryExportItemOutcome {
  readonly filename: string;
  readonly state: DirectoryExportItemState;
  readonly byteLength?: number;
  readonly sha256?: string;
  readonly reason?: string;
}

export type DirectoryExportState = "cancelled" | "completed" | "failed";

export interface DirectoryExportOutcome {
  readonly state: DirectoryExportState;
  readonly directory?: string;
  readonly items: readonly DirectoryExportItemOutcome[];
  readonly reason?: string;
}

interface PickDirectoryOptions {
  readonly title: string;
  readonly directory: true;
  readonly multiple: false;
  readonly recursive: false;
}

export interface NativeDirectoryExportAdapter {
  pickDirectory(options: PickDirectoryOptions): Promise<unknown>;
  exists(path: string): Promise<boolean>;
  writeFile(path: string, bytes: Uint8Array): Promise<void>;
  readFile(path: string): Promise<Uint8Array>;
  remove(path: string): Promise<void>;
}

function equalBytes(left: Uint8Array, right: Uint8Array): boolean {
  return left.byteLength === right.byteLength && left.every((value, index) => value === right[index]);
}

function failureReason(_error: unknown): string {
  return "Native file operation failed.";
}

/** Joins a native directory path with a filename using the same separator the directory already uses. */
export function joinNativeDirectoryPath(directory: string, filename: string): string {
  const separator = directory.includes("\\") && !directory.includes("/") ? "\\" : "/";
  const trimmedDirectory = directory.endsWith("/") || directory.endsWith("\\") ? directory.slice(0, -1) : directory;
  return `${trimmedDirectory}${separator}${filename}`;
}

/**
 * Disambiguates filenames that collide within one export batch (e.g. two callers that both
 * proposed `report.csv`) before any filesystem call, so a batch never spuriously refuses its own
 * second item as "already existing" just because the first item was written moments earlier.
 * Pre-existing files already on disk are still refused per item by `exists()` below.
 */
function withUniqueFilenames(
  artifacts: readonly DirectoryExportArtifact[]
): readonly DirectoryExportArtifact[] {
  const seen = new Map<string, number>();
  return artifacts.map((artifact) => {
    const count = seen.get(artifact.filename) ?? 0;
    seen.set(artifact.filename, count + 1);
    if (count === 0) return artifact;
    const dot = artifact.filename.lastIndexOf(".");
    const base = dot === -1 ? artifact.filename : artifact.filename.slice(0, dot);
    const extension = dot === -1 ? "" : artifact.filename.slice(dot);
    return { ...artifact, filename: `${base} (${count + 1})${extension}` };
  });
}

async function cleanupFailure(
  adapter: NativeDirectoryExportAdapter,
  path: string,
  filename: string,
  reason: string
): Promise<DirectoryExportItemOutcome> {
  try {
    await adapter.remove(path);
    return { filename, state: "failed", reason };
  } catch {
    return { filename, state: "cleanup-failed", reason: `${reason} Cleanup also failed.` };
  }
}

async function saveOneIntoDirectory(
  adapter: NativeDirectoryExportAdapter,
  directory: string,
  artifact: DirectoryExportArtifact
): Promise<DirectoryExportItemOutcome> {
  const path = joinNativeDirectoryPath(directory, artifact.filename);
  try {
    if (await adapter.exists(path)) return { filename: artifact.filename, state: "refused-existing" };
  } catch (error) {
    return { filename: artifact.filename, state: "failed", reason: failureReason(error) };
  }

  const ownedBytes = new Uint8Array(artifact.bytes);
  try {
    await adapter.writeFile(path, ownedBytes);
    const readBack = await adapter.readFile(path);
    const [expectedDigest, actualDigest] = await Promise.all([sha256Hex(ownedBytes), sha256Hex(readBack)]);
    if (!equalBytes(ownedBytes, readBack) || expectedDigest !== actualDigest) {
      return cleanupFailure(adapter, path, artifact.filename, "Saved file did not match the requested bytes and digest.");
    }
    return { filename: artifact.filename, state: "saved", byteLength: ownedBytes.byteLength, sha256: expectedDigest };
  } catch (error) {
    return cleanupFailure(adapter, path, artifact.filename, failureReason(error));
  }
}

async function exportOnce(
  adapter: NativeDirectoryExportAdapter,
  title: string,
  artifacts: readonly DirectoryExportArtifact[]
): Promise<DirectoryExportOutcome> {
  if (artifacts.length === 0) return { state: "completed", items: [] };
  let selected: unknown;
  try {
    selected = await adapter.pickDirectory({ title, directory: true, multiple: false, recursive: false });
  } catch (error) {
    return { state: "failed", items: [], reason: failureReason(error) };
  }
  if (selected === null || selected === undefined) return { state: "cancelled", items: [] };
  if (typeof selected !== "string" || selected === "") {
    return { state: "failed", items: [], reason: "Folder dialog returned an invalid directory target." };
  }

  const items: DirectoryExportItemOutcome[] = [];
  for (const artifact of withUniqueFilenames(artifacts)) {
    items.push(await saveOneIntoDirectory(adapter, selected, artifact));
  }
  return { state: "completed", directory: selected, items };
}

export interface NativeDirectoryExporter {
  exportAll(title: string, artifacts: readonly DirectoryExportArtifact[]): Promise<DirectoryExportOutcome>;
}

export function createNativeDirectoryExporter(adapter: NativeDirectoryExportAdapter): NativeDirectoryExporter {
  let active: Promise<DirectoryExportOutcome> | null = null;
  return {
    exportAll(title, artifacts) {
      if (active !== null) return active;
      active = exportOnce(adapter, title, artifacts).finally(() => {
        active = null;
      });
      return active;
    }
  };
}

let nativeExporterPromise: Promise<NativeDirectoryExporter> | null = null;

export async function exportNativeLocalDirectory(
  title: string,
  artifacts: readonly DirectoryExportArtifact[]
): Promise<DirectoryExportOutcome> {
  if (nativeExporterPromise === null) {
    nativeExporterPromise = Promise.all([
      import("@tauri-apps/plugin-dialog"),
      import("@tauri-apps/plugin-fs")
    ]).then(([{ open }, { exists, writeFile, readFile, remove }]) => (
      createNativeDirectoryExporter({
        pickDirectory: (options) => open(options),
        exists,
        writeFile,
        readFile,
        remove
      })
    )).catch((error: unknown) => {
      nativeExporterPromise = null;
      throw error;
    });
  }
  return (await nativeExporterPromise).exportAll(title, artifacts);
}
